import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AgentWardrobe from "./AgentWardrobe";
import "./AgentStudio.css";

const TEMPLATES = [
  { kind: "blank",     icon: "🧠", title: "Blank Agent",      seed: {} },
  { kind: "cfo",       icon: "💷", title: "CFO · Weekly Revenue",      seed: { name: "Cash", role: "CFO", purpose: "Compile a weekly cross-business revenue report (trading P&L + clothing + ads).", system_prompt: "You are Cash, the CFO. Numbers-first, brutal honesty, every figure cited. Format as a Monday briefing.", tools: ["finance","capital","stripe"], memory_kind: "semantic" } },
  { kind: "trade",     icon: "📈", title: "Trade Coach",                seed: { name: "Coach", role: "Trade Coach", purpose: "Review last week of trades, surface mistakes, suggest improvements.", system_prompt: "You are Coach. Stat-driven, blunt, focuses on the ONE habit that would have made the biggest difference this week.", tools: ["capital","tavily"], memory_kind: "vector" } },
  { kind: "designer",  icon: "🎨", title: "Snapdog Content",            seed: { name: "Eva", role: "Brand Designer", purpose: "Draft Instagram + TikTok posts for Snapdog clothing in the brand voice.", system_prompt: "You are Eva. Bold, street-cool, never corporate. Always 3 caption variants + 5 hashtags.", tools: ["instagram","tiktok"], memory_kind: "episodic" } },
  { kind: "support",   icon: "🎧", title: "Customer Support",           seed: { name: "Sarah", role: "Support", purpose: "Handle inbound messages with empathy.", system_prompt: "You are Sarah. Warm, fast, resolution-oriented. Escalate only if owner needed.", tools: ["gmail","telegram"], memory_kind: "episodic" } },
];

const STAGES = [
  { key: "purpose",      label: "1. Purpose & Scope" },
  { key: "prompt",       label: "2. System Prompt" },
  { key: "model",        label: "3. LLM Choice" },
  { key: "tools",        label: "4. Tools" },
  { key: "memory",       label: "5. Memory" },
  { key: "ui",           label: "6. UI Channels" },
  { key: "guardrails",   label: "7. Guardrails" },
  { key: "review",       label: "8. Review & Save" },
];

const TOOLS = ["chat","tavily","capital","stripe","gmail","calendar","telegram","instagram","tiktok","github","finance","ring"];
const MODELS = ["gpt-5.2","gpt-4o-mini","claude-sonnet-4.5","claude-haiku-4.5","gemini-3-pro","gemini-3-flash"];
const CHANNELS = ["voice","chat","email","slack","telegram"];
// ElevenLabs public voices the owner can pick per-agent. Empty string = use
// the main Jarvis voice. Adding more = paste any voice_id from your
// ElevenLabs voice library.
const VOICES = [
  { id: "",                     label: "(default — main Jarvis voice)" },
  { id: "onwK4e9ZLuTAKqWW03F9", label: "Daniel — deep British male" },
  { id: "XB0fDUnXU5powFXDhCwa", label: "Charlotte — warm British female" },
  { id: "AZnzlk1XvdvUeBnXmlld", label: "Domi — energetic female" },
  { id: "ErXwobaYiN019PkySvjV", label: "Antoni — clear factual male" },
  { id: "MF3mGyEYCl7XYWbV9V6O", label: "Elli — playful higher-pitched" },
  { id: "EXAVITQu4vr4xnSDxMaL", label: "Sarah — soft American female" },
  { id: "21m00Tcm4TlvDq8ikWAM", label: "Rachel — calm American female" },
  { id: "pNInz6obpgDQGcFmaJgB", label: "Adam — deep American male" },
  { id: "TxGEqnHWrfWFTfGW9XjX", label: "Josh — confident young male" },
  { id: "yoZ06aMxZJJ28mfd3POQ", label: "Sam — neutral male" },
];

export default function AgentStudio({ dismiss }) {
  const [agents, setAgents] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [tab, setTab] = useState("agents"); // agents | workflows
  const [mode, setMode] = useState("list");
  const [draft, setDraft] = useState(null);
  const [stage, setStage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [runMsg, setRunMsg] = useState("");
  const [runReply, setRunReply] = useState("");
  // Workflow builder draft
  const [wfDraft, setWfDraft] = useState(null);
  // 👕 Wardrobe modal — agent we're currently dressing (null = closed)
  const [wardrobeAgent, setWardrobeAgent] = useState(null);

  const refresh = async () => {
    const [aR, wR] = await Promise.all([api.get("/jarvis/agents"), api.get("/jarvis/workflows")]);
    setAgents(aR.data.agents || []);
    setWorkflows(wR.data.workflows || []);
  };
  useEffect(() => { refresh(); }, []);

  const startNew = (tmpl) => {
    setDraft({
      name: "", role: "", icon: "🤖", purpose: "", user_needs: "", success_criteria: "", constraints: "",
      system_prompt: "", persona: "", guardrails: "", model: "gpt-5.2", temperature: 0.7, context_window: 8000,
      voice_id: "",
      tools: [], memory_kind: "semantic", ui_channels: ["voice","chat"], enabled: true,
      ...tmpl.seed,
    });
    setStage(0); setMode("new");
  };

  const startEdit = (a) => { setDraft({ ...a }); setStage(0); setMode("edit"); };

  const save = async () => {
    setBusy(true);
    try {
      if (mode === "edit") await api.put(`/jarvis/agents/${draft.id}`, draft);
      else await api.post("/jarvis/agents", draft);
      await refresh();
      setMode("list"); setDraft(null);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this agent?")) return;
    await api.delete(`/jarvis/agents/${id}`);
    await refresh();
  };

  const runAgent = async (a) => { setDraft(a); setMode("run"); setRunMsg(""); setRunReply(""); };
  const invokeAgent = async () => {
    setBusy(true); setRunReply("");
    try {
      const r = await api.post(`/jarvis/agents/${draft.id}/invoke`, { message: runMsg });
      setRunReply(r.data.reply || "(no reply)");
      await refresh();
    } catch (e) { setRunReply(`Error: ${e.response?.data?.detail || e.message}`); }
    finally { setBusy(false); }
  };

  return (
    <div className="agent-studio" data-testid="agent-studio">
      {wardrobeAgent && (
        <AgentWardrobe
          agent={wardrobeAgent}
          onClose={() => setWardrobeAgent(null)}
          onSaved={(updated) => {
            setAgents(arr => arr.map(a => a.id === updated.id ? updated : a));
            setWardrobeAgent(null);
          }}
        />
      )}
      <div className="agent-studio-header">
        <div className="agent-studio-eyebrow">// AGENT STUDIO · {agents.length} AGENTS · {workflows.length} WORKFLOWS</div>
        <div className="agent-studio-title">YOUR AI TEAM</div>
        <div className="agent-tabs">
          <button className={`agent-tab ${tab === "agents" ? "is-active" : ""}`} onClick={() => { setTab("agents"); setMode("list"); }} data-testid="tab-agents">AGENTS</button>
          <button className={`agent-tab ${tab === "workflows" ? "is-active" : ""}`} onClick={() => { setTab("workflows"); setMode("list"); }} data-testid="tab-workflows">WORKFLOWS</button>
        </div>
      </div>

      {tab === "workflows" && mode === "list" && (
        <>
          <button className="agent-btn-primary" onClick={() => { setWfDraft({ name: "", description: "", steps: [], initial_input: "", schedule: "", enabled: true }); setMode("new-wf"); }} data-testid="new-workflow-btn">+ NEW WORKFLOW</button>
          <div className="agent-list" style={{ marginTop: 16 }}>
            {workflows.map((w) => (
              <div key={w.id} className="agent-card" data-testid={`workflow-card-${w.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="agent-card-head">
                  <span className="agent-card-icon">⛓</span>
                  <div>
                    <div className="agent-card-name">{w.name}</div>
                    <div className="agent-card-role">{w.steps.length} steps · {w.schedule || "manual"}</div>
                  </div>
                  <span className="agent-card-stats">{w.run_count || 0} runs</span>
                </div>
                <div className="agent-card-purpose">{w.description || "—"}</div>
                <div className="agent-card-actions">
                  <button className="agent-btn-primary" onClick={async () => { setBusy(true); try { const r = await api.post(`/jarvis/workflows/${w.id}/run`); alert(`Done.\n\nFinal output:\n${r.data.final_output.slice(0, 800)}`); await refresh(); } finally { setBusy(false); } }} disabled={busy}>{busy ? "RUNNING..." : "RUN NOW"}</button>
                  <button className="agent-btn" onClick={() => { setWfDraft({ ...w }); setMode("edit-wf"); }}>EDIT</button>
                  <button className="agent-btn agent-btn-danger" onClick={async () => { if (window.confirm("Delete workflow?")) { await api.delete(`/jarvis/workflows/${w.id}`); await refresh(); } }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "workflows" && (mode === "new-wf" || mode === "edit-wf") && wfDraft && (
        <div className="agent-wizard">
          <div className="agent-wizard-body">
            <Field label="Workflow name" v={wfDraft.name} on={(v) => setWfDraft({ ...wfDraft, name: v })} placeholder="e.g. Daily Snapdog Content Drop" />
            <Field label="Description" v={wfDraft.description} on={(v) => setWfDraft({ ...wfDraft, description: v })} multi placeholder="What does this chain do?" />
            <Field label="Initial input (what the first agent sees)" v={wfDraft.initial_input} on={(v) => setWfDraft({ ...wfDraft, initial_input: v })} multi placeholder="e.g. 'Find this week's top fashion trends'" />
            <Picker label="Schedule" options={["", "daily-8am", "weekly-mon-8am", "hourly"]} v={wfDraft.schedule} on={(v) => setWfDraft({ ...wfDraft, schedule: v })} />
            <div className="agent-field">
              <span className="agent-field-label">STEPS — output of each feeds into the next via {`{input}`}</span>
              {(wfDraft.steps || []).map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  <select value={s.agent_id} onChange={(e) => { const ns = [...wfDraft.steps]; ns[i] = { ...ns[i], agent_id: e.target.value }; setWfDraft({ ...wfDraft, steps: ns }); }} style={{ flex: "0 0 160px" }}>
                    <option value="">— pick agent —</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.icon} {a.name}</option>)}
                  </select>
                  <input value={s.instruction || ""} onChange={(e) => { const ns = [...wfDraft.steps]; ns[i] = { ...ns[i], instruction: e.target.value }; setWfDraft({ ...wfDraft, steps: ns }); }} placeholder="Instruction... use {input} for previous output" style={{ flex: 1 }} />
                  <button onClick={() => { const ns = wfDraft.steps.filter((_, j) => j !== i); setWfDraft({ ...wfDraft, steps: ns }); }} className="agent-btn agent-btn-danger" style={{ flex: "0 0 40px" }}>✕</button>
                </div>
              ))}
              <button onClick={() => setWfDraft({ ...wfDraft, steps: [...(wfDraft.steps || []), { agent_id: "", instruction: "{input}" }] })} className="agent-btn">+ ADD STEP</button>
            </div>
          </div>
          <div className="agent-wizard-foot">
            <button onClick={() => { setMode("list"); setWfDraft(null); }} className="agent-btn">CANCEL</button>
            <button onClick={async () => { setBusy(true); try { if (mode === "edit-wf") await api.put(`/jarvis/workflows/${wfDraft.id}`, wfDraft); else await api.post("/jarvis/workflows", wfDraft); await refresh(); setMode("list"); setWfDraft(null); } finally { setBusy(false); } }} disabled={busy || !wfDraft.name || !wfDraft.steps?.length} className="agent-btn-primary">{busy ? "SAVING..." : "✓ SAVE WORKFLOW"}</button>
          </div>
        </div>
      )}

      {tab === "agents" && mode === "list" && (
        <>
          <div className="agent-templates">
            {TEMPLATES.map((t) => (
              <button key={t.kind} className="agent-template" onClick={() => startNew(t)} data-testid={`agent-template-${t.kind}`}>
                <span className="agent-template-icon">{t.icon}</span>
                <span className="agent-template-title">{t.title}</span>
                <span className="agent-template-cta">+ Build</span>
              </button>
            ))}
          </div>
          <div className="agent-list">
            {agents.map((a) => (
              <div key={a.id} className="agent-card" data-testid={`agent-card-${a.name.toLowerCase()}`}>
                <div className="agent-card-head">
                  <span className="agent-card-icon">{a.icon || "🤖"}</span>
                  <div>
                    <div className="agent-card-name">{a.name}</div>
                    <div className="agent-card-role">{a.role}</div>
                  </div>
                  <span className="agent-card-stats">{a.invocations || 0} calls</span>
                </div>
                <div className="agent-card-purpose">{a.purpose || "—"}</div>
                <div className="agent-card-actions">
                  <button onClick={() => runAgent(a)} className="agent-btn-primary">RUN</button>
                  <button onClick={() => startEdit(a)} className="agent-btn">EDIT</button>
                  <button onClick={() => setWardrobeAgent(a)} className="agent-btn" data-testid={`agent-wardrobe-${a.id}`} title="Open wardrobe">👕</button>
                  <button onClick={() => remove(a.id)} className="agent-btn agent-btn-danger">✕</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "agents" && (mode === "new" || mode === "edit") && draft && (
        <div className="agent-wizard">
          <div className="agent-stages">
            {STAGES.map((s, i) => (
              <button key={s.key} onClick={() => setStage(i)} className={`agent-stage-tab ${i === stage ? "is-active" : ""}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="agent-wizard-body">
            {stage === 0 && (<>
              <Field label="Agent Name"      v={draft.name}             on={(v) => setDraft({ ...draft, name: v })} placeholder="e.g. Cash, Tom, Eva" />
              <Field label="Role"            v={draft.role}             on={(v) => setDraft({ ...draft, role: v })} placeholder="e.g. CFO, Developer, Trade Coach" />
              <Field label="Icon (emoji)"    v={draft.icon}             on={(v) => setDraft({ ...draft, icon: v })} placeholder="🤖" />
              <Field label="Purpose"         v={draft.purpose}          on={(v) => setDraft({ ...draft, purpose: v })} multi placeholder="What does this agent do?" />
              <Field label="Success criteria" v={draft.success_criteria} on={(v) => setDraft({ ...draft, success_criteria: v })} multi placeholder="How will you know it's working?" />
              <Field label="Constraints"     v={draft.constraints}      on={(v) => setDraft({ ...draft, constraints: v })} multi placeholder="What must it NOT do?" />
            </>)}
            {stage === 1 && (
              <Field label="System Prompt (the brain)" v={draft.system_prompt} on={(v) => setDraft({ ...draft, system_prompt: v })} multi big placeholder="You are <name>. Your job is..." />
            )}
            {stage === 2 && (<>
              <Picker label="Base Model" options={MODELS} v={draft.model} on={(v) => setDraft({ ...draft, model: v })} />
              <Field label="Temperature (0=strict, 1=creative)" v={draft.temperature} on={(v) => setDraft({ ...draft, temperature: parseFloat(v) || 0 })} />
              <Field label="Context window (tokens)"            v={draft.context_window} on={(v) => setDraft({ ...draft, context_window: parseInt(v) || 8000 })} />
            </>)}
            {stage === 3 && (
              <ChipPicker label="Tools the agent can use" options={TOOLS} selected={draft.tools} on={(arr) => setDraft({ ...draft, tools: arr })} />
            )}
            {stage === 4 && (
              <Picker label="Memory kind" options={["episodic","semantic","vector","sql","none"]} v={draft.memory_kind} on={(v) => setDraft({ ...draft, memory_kind: v })} />
            )}
            {stage === 5 && (
              <ChipPicker label="UI channels" options={CHANNELS} selected={draft.ui_channels} on={(arr) => setDraft({ ...draft, ui_channels: arr })} />
            )}
            {stage === 6 && (<>
              <Field label="Persona (tone, mannerisms)" v={draft.persona}    on={(v) => setDraft({ ...draft, persona: v })} multi placeholder="Friendly, witty, never apologises..." />
              <Field label="Guardrails (hard limits)"   v={draft.guardrails} on={(v) => setDraft({ ...draft, guardrails: v })} multi placeholder="Never give legal/financial advice. Never quote a number unless verified." />
              {/* 🎙 VOICE PICKER — owner asked: "can't change Tom's, Sarah's,
                  Eva's, Scout's, Dobby's voice." This dropdown lets him
                  pick any ElevenLabs voice from the VOICES list above. */}
              <Picker
                label="🎙 ElevenLabs Voice"
                options={VOICES.map(v => v.id)}
                v={draft.voice_id || ""}
                on={(v) => setDraft({ ...draft, voice_id: v })}
              />
              <div style={{ fontSize: 10, opacity: 0.6, marginTop: -8 }}>
                {VOICES.find(v => v.id === (draft.voice_id || ""))?.label || "(custom voice id)"}
              </div>
              <Field
                label="Or paste a custom ElevenLabs voice_id"
                v={draft.voice_id || ""}
                on={(v) => setDraft({ ...draft, voice_id: v.trim() })}
                placeholder="e.g. onwK4e9ZLuTAKqWW03F9 (from your ElevenLabs library)"
              />
            </>)}
            {stage === 7 && (
              <div className="agent-review">
                <h4>{draft.icon} {draft.name} <span className="opacity-50">· {draft.role}</span></h4>
                <p className="opacity-70 text-[11px] mt-2">{draft.purpose}</p>
                <pre className="agent-review-json">{JSON.stringify(draft, null, 2)}</pre>
              </div>
            )}
          </div>
          <div className="agent-wizard-foot">
            <button onClick={() => { setMode("list"); setDraft(null); }} className="agent-btn">CANCEL</button>
            {stage > 0 && <button onClick={() => setStage(stage - 1)} className="agent-btn">← BACK</button>}
            {stage < STAGES.length - 1
              ? <button onClick={() => setStage(stage + 1)} className="agent-btn-primary">NEXT →</button>
              : <button onClick={save} disabled={busy || !draft.name} className="agent-btn-primary">{busy ? "SAVING..." : "✓ DEPLOY AGENT"}</button>}
          </div>
        </div>
      )}

      {tab === "agents" && mode === "run" && draft && (
        <div className="agent-run">
          <div className="agent-run-head">
            <span style={{fontSize: 32}}>{draft.icon}</span>
            <div>
              <div className="agent-card-name">{draft.name}</div>
              <div className="agent-card-role">{draft.role}</div>
            </div>
            <button onClick={() => { setMode("list"); setDraft(null); }} className="agent-btn">← BACK</button>
          </div>
          <textarea className="agent-run-input" value={runMsg} onChange={(e) => setRunMsg(e.target.value)} placeholder={`Ask ${draft.name}...`} rows={3} />
          <button onClick={invokeAgent} disabled={busy || !runMsg.trim()} className="agent-btn-primary agent-run-fire">{busy ? "THINKING..." : `→ ASK ${draft.name.toUpperCase()}`}</button>
          {runReply && <div className="agent-run-reply"><div className="agent-run-reply-label">// {draft.name.toUpperCase()} REPLIES</div>{runReply}</div>}
        </div>
      )}
    </div>
  );
}

function Field({ label, v, on, placeholder, multi, big }) {
  return (
    <label className="agent-field">
      <span className="agent-field-label">{label}</span>
      {multi
        ? <textarea value={v || ""} onChange={(e) => on(e.target.value)} placeholder={placeholder} rows={big ? 8 : 3} />
        : <input value={v || ""} onChange={(e) => on(e.target.value)} placeholder={placeholder} />}
    </label>
  );
}
function Picker({ label, options, v, on }) {
  return (
    <label className="agent-field">
      <span className="agent-field-label">{label}</span>
      <select value={v} onChange={(e) => on(e.target.value)}>{options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
    </label>
  );
}
function ChipPicker({ label, options, selected, on }) {
  const toggle = (o) => { const s = new Set(selected); s.has(o) ? s.delete(o) : s.add(o); on([...s]); };
  return (
    <div className="agent-field">
      <span className="agent-field-label">{label}</span>
      <div className="agent-chip-row">
        {options.map((o) => (
          <button key={o} type="button" onClick={() => toggle(o)} className={`agent-chip ${selected.includes(o) ? "is-on" : ""}`}>{o}</button>
        ))}
      </div>
    </div>
  );
}
